<template>
  <div>
    <b-card>
      <h4 class="mb-1">Zonas de cobertura</h4>
      <p class="text-muted">
        Selecciona una farmacia y dibuja sus zonas de domicilio en el mapa. Cada zona puede cobrar un
        <strong>precio fijo</strong> (mismo valor en toda el área) o <strong>por distancia</strong> (precio por km).
        Una farmacia puede combinar ambos tipos.
      </p>

      <!-- Selector de farmacia -->
      <b-form-group label="Farmacia" class="w-100 w-md-50">
        <v-select
          label="name"
          v-model="selectedCustomer"
          :options="customersOptions"
          placeholder="Selecciona una farmacia"
          @input="onCustomerChange"
        />
      </b-form-group>

      <template v-if="selectedCustomer">
        <b-alert :show="!hasLocation" variant="warning" class="p-1">
          Esta farmacia aún no tiene una ubicación registrada. Puedes dibujar zonas igualmente, pero se recomienda
          fijar primero su ubicación en el módulo de Clientes.
        </b-alert>

        <!-- Controles de dibujo -->
        <div class="d-flex flex-wrap gap-2 mb-1 align-items-center">
          <b-button v-if="!drawing" variant="primary" @click="startDrawing">
            <feather-icon icon="Edit2Icon" size="16" class="mr-25" />
            Dibujar nueva zona
          </b-button>
          <template v-else>
            <b-badge variant="light-primary" class="p-75">
              Haz clic en el mapa para marcar las esquinas del área ({{ drawingPoints.length }} puntos)
            </b-badge>
            <b-button variant="success" :disabled="drawingPoints.length < 3" @click="finishDrawing">
              Terminar zona
            </b-button>
            <b-button variant="outline-secondary" @click="undoPoint" :disabled="!drawingPoints.length">
              Deshacer punto
            </b-button>
            <b-button variant="outline-danger" @click="cancelDrawing">Cancelar</b-button>
          </template>
        </div>

        <!-- Mapa -->
        <GmapMap
          ref="map"
          :center="center"
          :zoom="13"
          style="width: 100%; height: 480px"
          :options="mapOptions"
          @click="onMapClick"
        >
          <GmapMarker
            v-if="hasLocation"
            :position="center"
            :icon="{ url: markerIcon, scaledSize: { width: 32, height: 32 } }"
          />
          <!-- Zonas existentes -->
          <GmapPolygon
            v-for="zone in coverages"
            :key="zone.id"
            :paths="zone.paths"
            :options="polygonOptions(zone)"
          />
          <!-- Zona en construcción -->
          <GmapPolygon
            v-if="drawing && drawingPoints.length >= 3"
            :paths="drawingPoints"
            :options="{ fillColor: '#FF9F43', fillOpacity: 0.3, strokeColor: '#FF9F43', strokeWeight: 2, clickable: false }"
          />
          <GmapPolyline
            v-if="drawing && drawingPoints.length >= 2 && drawingPoints.length < 3"
            :path="drawingPoints"
            :options="{ strokeColor: '#FF9F43', strokeWeight: 2 }"
          />
        </GmapMap>

        <!-- Tabla de zonas -->
        <b-table
          class="mt-2"
          :items="coverages"
          :fields="tableColumns"
          responsive
          show-empty
          empty-text="Esta farmacia no tiene zonas de cobertura todavía"
        >
          <template #cell(pricingType)="data">
            <b-badge :variant="data.item.pricingType === 'fixed' ? 'primary' : 'success'">
              {{ data.item.pricingType === 'fixed' ? 'Precio fijo' : 'Por distancia' }}
            </b-badge>
          </template>
          <template #cell(value)="data">
            <span v-if="data.item.pricingType === 'fixed'">$ {{ formatMoney(data.item.deliveryCost) }}</span>
            <span v-else>$ {{ formatMoney(data.item.pricePerKm) }} / km</span>
          </template>
          <template #cell(actions)="data">
            <b-button size="sm" variant="flat-primary" @click="openEditForm(data.item)">
              <feather-icon icon="EditIcon" size="16" />
            </b-button>
            <b-button size="sm" variant="flat-danger" @click="deleteZone(data.item)">
              <feather-icon icon="Trash2Icon" size="16" />
            </b-button>
          </template>
        </b-table>
      </template>
    </b-card>

    <!-- Modal de zona -->
    <b-modal
      v-model="showModal"
      :title="form.id ? 'Editar zona' : 'Nueva zona'"
      centered
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="saving || !isFormValid"
      @ok.prevent="saveZone"
      @hidden="resetForm"
    >
      <b-form-group label="Nombre de la zona (opcional)">
        <b-form-input v-model="form.name" placeholder="Ej. Casco urbano, San Pablo de Borbur" />
      </b-form-group>

      <b-form-group label="Tipo de cobro">
        <b-form-radio-group v-model="form.pricing_type" stacked>
          <b-form-radio value="fixed">Precio fijo (mismo valor en toda la zona)</b-form-radio>
          <b-form-radio value="distance">Por distancia (precio por kilómetro)</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group v-if="form.pricing_type === 'fixed'" label="Precio del domicilio (COP)">
        <b-form-input v-model.number="form.delivery_cost" type="number" min="0" placeholder="Ej. 5000" />
      </b-form-group>

      <b-form-group v-else label="Precio por kilómetro (COP)">
        <b-form-input v-model.number="form.price_per_km" type="number" min="0" placeholder="Ej. 1000" />
      </b-form-group>

      <b-alert :show="!form.paths || form.paths.length < 3" variant="danger" class="p-1 mb-0">
        Esta zona no tiene un área válida dibujada.
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { gmapApi } from 'vue2-google-maps';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CoveragesView',
  components: { vSelect },
  data() {
    return {
      customersOptions: [],
      selectedCustomer: null,
      hasLocation: false,
      center: { lat: 4.65, lng: -74.08 }, // centro por defecto (Colombia)
      coverages: [],
      markerIcon: 'https://mimedicamento.com.co/img/logo.3a7c8281.svg',
      mapOptions: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      },
      drawing: false,
      drawingPoints: [], // vértices del polígono en construcción
      showModal: false,
      saving: false,
      form: this.emptyForm(),
      tableColumns: [
        { key: 'name', label: 'Zona' },
        { key: 'pricingType', label: 'Tipo' },
        { key: 'value', label: 'Valor' },
        { key: 'actions', label: 'Acciones' },
      ],
    };
  },
  computed: {
    google() {
      return gmapApi() || null;
    },
    isFormValid() {
      if (!this.form.paths || this.form.paths.length < 3) return false;
      if (this.form.pricing_type === 'fixed') return this.form.delivery_cost >= 0 && this.form.delivery_cost !== null && this.form.delivery_cost !== '';
      return this.form.price_per_km >= 0 && this.form.price_per_km !== null && this.form.price_per_km !== '';
    },
  },
  methods: {
    emptyForm() {
      return { id: null, name: null, paths: null, pricing_type: 'fixed', delivery_cost: null, price_per_km: null };
    },
    formatMoney(value) {
      const n = Number(value || 0);
      return n.toLocaleString('es-CO');
    },
    polygonOptions(zone) {
      const color = zone.pricingType === 'fixed' ? '#009EE0' : '#28C76F';
      return { fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeWeight: 2, clickable: false };
    },
    async fetchCustomers() {
      // Endpoint filtrado por rol: admin ve todas; los demás solo sus farmacias.
      const { data } = await this.$http.get('/users/get-customers');
      this.customersOptions = Array.isArray(data) ? data : (data.customersOptions || []);
    },
    async onCustomerChange() {
      this.cancelDrawing();
      this.coverages = [];
      this.hasLocation = false;
      if (!this.selectedCustomer) return;
      await Promise.all([this.loadLocation(), this.loadCoverages()]);
    },
    async loadLocation() {
      try {
        const { data } = await this.$http.get('/customers/get-location/' + this.selectedCustomer.id);
        if (data.customerLocation && data.customerLocation.location && data.customerLocation.location.lat) {
          this.center = data.customerLocation.location;
          this.hasLocation = true;
        }
      } catch (e) {
        this.hasLocation = false;
      }
    },
    async loadCoverages() {
      const { data } = await this.$http.get('/customers/get-coverages/' + this.selectedCustomer.id);
      this.coverages = data.customerCoverages || [];
    },
    // --- Dibujo manual de polígonos (sin DrawingManager, removido por Google en v3.65) ---
    startDrawing() {
      this.drawing = true;
      this.drawingPoints = [];
    },
    onMapClick(event) {
      if (!this.drawing || !event || !event.latLng) return;
      this.drawingPoints.push({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    },
    undoPoint() {
      this.drawingPoints.pop();
    },
    finishDrawing() {
      if (this.drawingPoints.length < 3) {
        this.notify('Faltan puntos', 'Marca al menos 3 esquinas para formar la zona.', 'danger');
        return;
      }
      const paths = this.drawingPoints.slice();
      this.drawing = false;
      this.drawingPoints = [];
      this.openCreateForm(paths);
    },
    cancelDrawing() {
      this.drawing = false;
      this.drawingPoints = [];
    },
    openCreateForm(paths) {
      this.form = { ...this.emptyForm(), paths };
      this.showModal = true;
    },
    openEditForm(zone) {
      this.form = {
        id: zone.id,
        name: zone.name,
        paths: zone.paths,
        pricing_type: zone.pricingType || 'fixed',
        delivery_cost: zone.deliveryCost,
        price_per_km: zone.pricePerKm,
      };
      this.showModal = true;
    },
    resetForm() {
      this.form = this.emptyForm();
      this.saving = false;
    },
    async saveZone() {
      if (!this.isFormValid) return;
      this.saving = true;
      const payload = {
        name: this.form.name,
        paths: this.form.paths,
        pricing_type: this.form.pricing_type,
        delivery_cost: this.form.pricing_type === 'fixed' ? this.form.delivery_cost : null,
        price_per_km: this.form.pricing_type === 'distance' ? this.form.price_per_km : null,
      };
      try {
        if (this.form.id) {
          await this.$http.put(`/coverages/${this.form.id}`, payload);
        } else {
          await this.$http.post(`/customers/${this.selectedCustomer.id}/coverages`, payload);
        }
        this.showModal = false;
        await this.loadCoverages();
        this.notify('Zona guardada', 'La zona de cobertura se guardó correctamente.', 'success');
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No se pudo guardar la zona.';
        this.notify('Error', msg, 'danger');
      } finally {
        this.saving = false;
      }
    },
    async deleteZone(zone) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `¿Eliminar la zona "${zone.name || 'sin nombre'}"?`,
        { title: 'Confirmar', okVariant: 'danger', okTitle: 'Eliminar', cancelTitle: 'Cancelar', centered: true }
      );
      if (!ok) return;
      try {
        await this.$http.delete(`/coverages/${zone.id}`);
        await this.loadCoverages();
        this.notify('Zona eliminada', 'La zona se eliminó correctamente.', 'success');
      } catch (error) {
        this.notify('Error', 'No se pudo eliminar la zona.', 'danger');
      }
    },
    notify(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: { title, text, variant, icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon' },
      });
    },
  },
  created() {
    this.fetchCustomers();
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
